import isEmpty from 'lodash-es/isEmpty'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../constants/common'
import { getItemsIds } from '../../../../utils/common'
import { DocFlowSendingFieldName } from '../DocFlowSending.constants'
import {
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SAVE_TO_PROFILE,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_ADDITIONAL_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SAVE_TO_PROFILE,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_ADDITIONAL_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EMAIL,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EPOST,
  getSendingCommunicationTableMassActions,
} from '../DocFlowSendingCommunicationTable'

import {
  DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_AVAILABLE_MASS_ACTIONS,
  DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_COLUMNS,
  DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_NOTIFICATION_ID,
  DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_NOTIFICATION_ID_EMPTY_COMMUNICATION,
} from './DocFlowSendingEpostValidationErrorsReportModal.constants'

function partitionByTruthy(items) {
  return Object.entries(items).reduce(
    (acc, [key, value]) => {
      if (value) {
        acc.activate.push(key)
      } else {
        acc.deactivate.push(key)
      }

      return acc
    },
    { activate: [], deactivate: [] }
  )
}

function isNotDirtyCommunicationType({ activate, cellName, deactivate, item }) {
  return (
    deactivate.includes(String(item.id)) ||
    (!item[cellName] && !activate.includes(String(item.id)))
  )
}

export function getDocFlowValidationErrorsModalColumns(columns) {
  return columns
    ? Object.entries(
        DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_COLUMNS
      ).map(([key, value], index) => ({
        ...value,
        index,
        name: key,
        ordering_field: columns[key].ordering_field,
        title: columns[key].title,
        visible: true,
      }))
    : EMPTY_LIST
}

export function getDocFlowValidationErrorsModalNotifications(t) {
  return {
    emptyCommunications: {
      id: DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_NOTIFICATION_ID_EMPTY_COMMUNICATION,
      text: t(
        'DocFlow.EpostValidationErrorsModalEmptyCommunicationErrorNotificationKey'
      ),
      type: NOTIFICATION_TYPE.error,
    },
  }
}

export function getDocFlowValidationErrorsModalMassActions(t) {
  return getSendingCommunicationTableMassActions(t).filter(item =>
    DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_AVAILABLE_MASS_ACTIONS.includes(
      item.value
    )
  )
}

export function getDocFlowValidationErrorsModalUpdatedSingleActionItems({
  cellName,
  item,
  previous,
  value,
}) {
  const current = { ...previous }

  if (item[cellName] === value) {
    delete current[item.id]
  } else {
    current[item.id] = value
  }

  return current
}

export function getDocFlowValidationErrorsModalPreparedFilterItemsToBackend({
  saveToProfile,
  sendAdditionalEmail,
  sendEmail,
}) {
  return {
    saveToProfilePreparedItems: partitionByTruthy(saveToProfile),
    sendAdditionalEmailPreparedItems: partitionByTruthy(sendAdditionalEmail),
    sendEmailPreparedItems: partitionByTruthy(sendEmail),
  }
}

export function checkDocFlowValidationErrorsModalEmptyCommunicationTypes({
  items,
  saveToProfile,
  sendAdditionalEmail,
  sendEmail,
}) {
  const emptyCommunicationRecipients = items.filter(item => {
    const saveToProfileEmpty = isNotDirtyCommunicationType({
      ...saveToProfile,
      cellName: DocFlowSendingFieldName.SaveToProfile,
      item,
    })

    const sendEmailEmpty = isNotDirtyCommunicationType({
      ...sendEmail,
      cellName: DocFlowSendingFieldName.SendEmail,
      item,
    })

    // The backend treats send_email and send_additional_email as one channel,
    // so a recipient kept on additional addresses alone still has a fallback.
    const sendAdditionalEmailEmpty = isNotDirtyCommunicationType({
      ...sendAdditionalEmail,
      cellName: DocFlowSendingFieldName.SendAdditionalEmail,
      item,
    })

    return saveToProfileEmpty && sendEmailEmpty && sendAdditionalEmailEmpty
  })

  return getItemsIds(emptyCommunicationRecipients)
}

export function hasDocFlowValidationErrorsModalChanges({
  saveToProfile,
  selectAllPages,
  selected,
  sendAdditionalEmail,
  sendEmail,
}) {
  return (
    selected.length > 0 ||
    selectAllPages ||
    !isEmpty(saveToProfile) ||
    !isEmpty(sendEmail) ||
    !isEmpty(sendAdditionalEmail)
  )
}

export function getDocFlowValidationErrorsModalPreparedItemsLegth(
  preparedItems
) {
  return preparedItems.activate.length + preparedItems.deactivate.length
}

export function isDocFlowValidationErrorsModalSendEpostOff(items) {
  return items.every(item => !item[DocFlowSendingFieldName.SendEpost])
}

export function getDocFlowValidationErrorsModalSubmitParams({
  items,
  saveToProfile,
  sendAdditionalEmail,
  sendEmail,
}) {
  const actionToFilters = {}

  if (!isDocFlowValidationErrorsModalSendEpostOff(items)) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EPOST
    ] = {
      id: getItemsIds(items),
    }
  }

  if (saveToProfile.activate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SAVE_TO_PROFILE
    ] = {
      id: saveToProfile.activate,
    }
  }

  if (saveToProfile.deactivate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SAVE_TO_PROFILE
    ] = {
      id: saveToProfile.deactivate,
    }
  }

  if (sendEmail.activate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_EMAIL
    ] = {
      id: sendEmail.activate,
    }
  }

  if (sendEmail.deactivate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_EMAIL
    ] = {
      id: sendEmail.deactivate,
    }
  }

  if (sendAdditionalEmail.activate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_ACTIVATE_SEND_ADDITIONAL_EMAIL
    ] = {
      id: sendAdditionalEmail.activate,
    }
  }

  if (sendAdditionalEmail.deactivate.length > 0) {
    actionToFilters[
      DOC_FLOW_SENDING_COMMUNICATION_TABLE_MASS_ACTION_DEACTIVATE_SEND_ADDITIONAL_EMAIL
    ] = {
      id: sendAdditionalEmail.deactivate,
    }
  }

  return actionToFilters
}

export function getDocFlowValidationErrorsModalIdsLengthToSave({
  items,
  saveToProfilePreparedItems,
  sendAdditionalEmailPreparedItems,
  sendEmailPreparedItems,
}) {
  const lengthWithoutEpost =
    getDocFlowValidationErrorsModalPreparedItemsLegth(
      saveToProfilePreparedItems
    ) +
    getDocFlowValidationErrorsModalPreparedItemsLegth(sendEmailPreparedItems) +
    getDocFlowValidationErrorsModalPreparedItemsLegth(
      sendAdditionalEmailPreparedItems
    )

  if (isDocFlowValidationErrorsModalSendEpostOff(items)) {
    return lengthWithoutEpost
  }

  return lengthWithoutEpost + items.length
}

export function getDocFlowValidationErrorsModalNotification(t) {
  return {
    id: DOC_FLOW_SENDING_EPOST_VALIDATION_ERRORS_REPORT_MODAL_NOTIFICATION_ID,
    text: t('DocFlow.EpostValidationErrorsModalNotificationInfoTextKey'),
    type: NOTIFICATION_TYPE.info,
  }
}
